import { LocalStorageService } from "./services/shared/local-storage/local-storage.service";
import { Component, HostListener, Inject, OnInit, PLATFORM_ID } from "@angular/core";
import { CommonModule, isPlatformBrowser, Location } from "@angular/common";
import { SessionService } from "src/app/services/shared/session/session.service";
import { SessionDialogComponent } from "src/app/components/shared/session-dialog/session-dialog.component";
import { MatDialog } from "@angular/material/dialog";

import { GenerateTokenService } from "integrationpingid-lib";
import { Router, RouterOutlet } from "@angular/router";
import { SharedModule } from "./components/shared/shared.module";

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [
    CommonModule,
    RouterOutlet,
    SharedModule
  ],
  standalone: true
})
export class AppComponent implements OnInit {
  isBrowser: boolean;
  private sessionWarningSubscriptionInitialized = false;

  private isEntryRoute(path: string): boolean {
    return path === '/callback'
      || path === '/login'
      || path.startsWith('/externo/token')
      || path.startsWith('/interno/token/rds/')
      || path.startsWith('/externo/source')
      || path.startsWith('/int/source/dm');
  }

  constructor(
    private location: Location,
    private sessionService: SessionService,
    private router: Router,
    public dialogo: MatDialog,
    public serviceToken: GenerateTokenService,
    private localStorageService: LocalStorageService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    if (this.isBrowser) {
      // Detecta si la carga actual es una recarga, escritura de URL en la barra,
      // click en favorito o navegacion atras/adelante. En esos casos limpia
      // localStorage + sessionStorage para evitar inconsistencias entre el estado
      // en memoria (perdido) y el persistido (viejo) que rompian DM.
      const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
      const navType = navEntries?.[0]?.type;

      // Las rutas de entrada al login NO se deben tocar: necesitan localStorage
      // (clientId, codeVerifier, redirectUri, state, urlToken, init) y/o el queryParam
      // intactos para completar el flujo OAuth/OIDC y poblar la sesion.
      const path = window.location.pathname;
      const isEntryRoute = this.isEntryRoute(path);

      if (!isEntryRoute) {
        sessionStorage.clear();

        // F5 ('reload'), escribir URL/click favorito ('navigate')
        if (navType === 'reload' || navType === 'navigate') {
          localStorage.clear();
          if (path !== '/') {
            window.location.replace('/');
          }
        }
      }
    }

  }

  @HostListener("window:beforeunload", ["$event"])
  handleBeforeUnload(event: BeforeUnloadEvent) {
    // Guarda en session si es vip (true) en la variable okt cuando cierra o actualiza la pagina.
    localStorage.setItem("okt", (this.localStorageService.getIsVip ? "true" : "false"));
  }

  hasRouter(): boolean {
    if (
      this.location.path() === "/login" ||
      this.location.path() === "/external-login" ||
      // this.location.path() === "/externo/token" ||
      this.location.path() === "/management/health"
    ) {
      return false;
    } else {
      return true;
    }
  }

  validaFlow() {
    // Lee la variable, valida si existe y que valor tiene para determinar el flujo.
    const miVariable = localStorage.getItem('okt');
    if (miVariable) {
      this.localStorageService.setIsVip = miVariable === 'true';
    }
  }

  ngOnInit(): void {
    this.validaFlow();

    const path = window.location.pathname;
    // Siempre registrar la suscripción del warning para que el modal funcione
    // aunque la sesión se inicie desde otro componente (ej: /externo/token OIDC).
    this.handleSession(!this.isEntryRoute(path));

    // Verificar si el usuario viene de una renovación automática de OIDC
    const wasAutoRenew = localStorage.getItem('oidc-auto-renew') === 'true';
    if (wasAutoRenew) {
      console.log('[AppComponent] Renovación automática completada, limpiando flags');
      const returnUrl = localStorage.getItem('oidc-return-url');
      localStorage.removeItem('oidc-auto-renew');
      localStorage.removeItem('oidc-return-url');

      // Retornar a la URL guardada (si existe)
      if (returnUrl && returnUrl !== '/') {
        this.router.navigateByUrl(returnUrl);
      }
    }
  }

  handleSession(startMonitor: boolean = true) {
    if (!this.sessionWarningSubscriptionInitialized) {
      this.sessionWarningSubscriptionInitialized = true;
      console.log('[AppComponent] Registrando listener onEndSession() para modal de sesión');

      this.sessionService.onEndSession().subscribe(() => {
      console.log('[AppComponent] Sesión finalizada, mostrando modal de confirmación');
      console.log('[Session Debug] onEndSession emitido. dialogs abiertos antes de open:', this.dialogo.openDialogs.length, '| route:', this.router.url);
      console.log('[Session Flow] Modal warning en flujo:', this.sessionService.isVipSession() ? 'VIP/OIDC' : 'INTERNO/PingID');

      const DialogRef = this.dialogo.open(SessionDialogComponent, {
        width: "500px",
        disableClose: true,
      });

      console.log('[Session Debug] dialog.open ejecutado. dialogId:', DialogRef.id, '| dialogs abiertos después de open:', this.dialogo.openDialogs.length);

      DialogRef.afterOpened().subscribe(() => {
        console.log('[Session Debug] SessionDialog afterOpened. dialogId:', DialogRef.id, '| route:', this.router.url);
      });

      DialogRef.afterClosed().subscribe((res) => {
        console.log('[Session Debug] SessionDialog afterClosed. result:', res, '| dialogs abiertos restantes:', this.dialogo.openDialogs.length, '| route:', this.router.url);
        if (res === undefined || res === null) {
          console.warn('[Session Debug] Modal cerrado sin acción explícita del usuario. Revisar llamados a dialog.closeAll().');
        }

        if (res == 1) {
          // Usuario aceptó renovar sesión
          console.log('[AppComponent] Usuario aceptó renovar sesión');
          if (this.sessionService.isVipSession()) {
            console.log('[Session Flow] Acción continuar -> VIP/OIDC (refresh interno sin redirección)');
            this.sessionService.handleContinueVipSession();
          } else {
            console.log('[Session Flow] Acción continuar -> INTERNO/PingID (handleRefreshPingId)');
            this.sessionService.handleRefreshPingId();
            // Reiniciar monitoreo de sesión después de renovación PingID
            setTimeout(() => {
              this.sessionService.startSession();
            }, 1000);
          }
        } else if (res == 0) {
          // Usuario rechazó: hacer logout
          console.log('[AppComponent] Usuario rechazó renovación, ejecutando logout');
          this.sessionService.endSession();
          this.sessionService.handleLogout();
        }
      });
      });
    }

    if (startMonitor) {
      console.log('[AppComponent] Iniciando sesión');
      this.sessionService.startSession();
    } else {
      console.log('[AppComponent] Entry route detectada: se omite startSession() aquí (se inicia desde flujo de autenticación).');
    }
  }

  muestra: boolean = false;
  handleBtn() {
    this.muestra = !this.muestra;
  }

}
